'use client';

import { useRouter } from '@bprogress/next/app';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';

import { apiDadata } from '@/fo/api/dadata';
import { customerService } from '@/fo/api/magento';
import { UiKitDrawerSearch } from '@/fo/ui/kit/drawer/search';
import { utilEventCityModal } from '@/fo/util/event-city-modal';

import {
  INITIAL_CITIES,
  mapCitiesFromDadata,
  removeNonCity,
  Suggestion
} from '../utils/cities';

type DrawerProps = (
  | {
      mode: 'address';
      open: boolean;
      setOpen: (open: boolean) => void;
      onSelectCity: (city: Suggestion) => Promise<void> | void;
    }
  | {
      mode: 'location';
      open?: never;
      setOpen?: never;
      onSelectCity?: never;
    }
) & {
  defaultCity?: string;
  initialCities?: Suggestion[];
};

export function FeatureCityModalBase({
  defaultCity,
  mode,
  open,
  setOpen,
  onSelectCity,
  initialCities
}: DrawerProps) {
  const router = useRouter();

  const [value, setValue] = useState(defaultCity || '');
  const [openState, setOpenState] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>(
    initialCities || INITIAL_CITIES
  );
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const t = useTranslations();

  const openDrawer = mode === 'address' ? open : openState;
  const setOpenDrawer = mode === 'address' ? setOpen : setOpenState;

  useEffect(() => {
    const unsubscribe =
      mode === 'location'
        ? utilEventCityModal.onOpen(() => setOpenDrawer(true))
        : undefined;

    return unsubscribe;
  }, [mode, setOpenDrawer]);

  const handleChange = useCallback((val: string) => {
    setValue(val);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!val) {
      setSuggestions(INITIAL_CITIES);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      const resp = await apiDadata('addressSuggestion', {
        query: val,
        from_bound: { value: 'city' },
        to_bound: { value: 'settlement' },
        restrict_value: false
      });

      if ('data' in resp && Array.isArray(resp.data.suggestions)) {
        const items = removeNonCity(resp.data.suggestions);

        setSuggestions(mapCitiesFromDadata(items));
      } else {
        setSuggestions([]);
      }
    }, 400);
  }, []);

  const onSelect = async (item: Suggestion) => {
    setLoading(true);
    setValue(item.value);

    if (mode === 'location') {
      await customerService.setCity(item);
      router.refresh();
    }

    if (mode === 'address') {
      await onSelectCity?.(item);
    }

    setOpenDrawer(false);
    setLoading(false);
  };

  return (
    <UiKitDrawerSearch
      open={openDrawer}
      onOpenChange={setOpenDrawer}
      title={t('cityModal.title')}
      value={value}
      onChange={handleChange}
      suggestions={suggestions}
      onSuggestionClick={onSelect}
      loading={loading}
      autoFocus={false}
    />
  );
}
